/**
 * TopBar — direct port of the prototype's `.topbar` (cms-redesign-mockup.html
 * ~line 123). Every Tailwind/inline-style value below pairs 1:1 with a CSS
 * declaration from the prototype; the comments call out the source rule.
 *
 * Intentional deviations from the prototype:
 *   • Help icon button — dropped (no-op in the prototype demo).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SessionHelper from '../Helper/SessionHelper';
import RouteConfig from '../../constants/RouteConfig';
import { useRedesignFlag } from '../../utils/redesignFlag';
import { useThemeMode } from '../../utils/themeMode';
import { useFontScale } from '../../utils/fontScale';
import { TtsSwirl } from './TtsSwirl';
import BrandIcon from './BrandIcon';
import { cn } from './cn';

/* ─── Helpers ─────────────────────────────────────────────────────────── */

function userInitials(user) {
  if (!user) return '?';
  const name = user.admin_name || user.admin_email || '';
  return (
    name
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '?'
  );
}

function firstName(user) {
  if (!user) return '';
  const name = user.admin_name || user.admin_email || '';
  return name.split(/\s+|@/)[0] || '';
}

/** `.avatar` (26px) and `.user-menu .avatar-lg` (44px) — same disc, different
 *  size/font. Renders `admin_image` if present, initials fallback otherwise.
 *  If the image URL is present but fails to load (broken/404/expired token),
 *  flips state and shows the initials so the disc is never empty. */
function Avatar({ user, size, fontSize }) {
  const src = user?.admin_image;
  const [broken, setBroken] = useState(false);
  // Reset broken-state whenever the src changes (e.g. user switches account).
  useEffect(() => { setBroken(false); }, [src]);
  const showImage = !!src && !broken;
  return (
    <span
      className={cn(
        'inline-grid place-items-center rounded-full shrink-0 overflow-hidden',
        'bg-teal-green text-white font-condensed font-bold',
      )}
      style={{ width: size, height: size, fontSize }}
    >
      {showImage ? (
        <img
          src={src}
          alt={user?.admin_name || 'Avatar'}
          className="w-full h-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        userInitials(user)
      )}
    </span>
  );
}

/** Resolve crumbs from the current pathname against RouteConfig. */
function crumbsFor(pathname) {
  const exact = RouteConfig.find((r) => r.path === pathname);
  if (exact) return [{ label: exact.label, here: true }];

  const matches = RouteConfig.filter(
    (r) => r.path && r.label && pathname.startsWith(r.path + '/'),
  ).sort((a, b) => b.path.length - a.path.length);

  if (matches.length) {
    const parent = matches[0];
    const last = pathname.split('/').filter(Boolean).slice(-1)[0] || '';
    const hereLabel = /^\d+$/.test(last) ? `#${last}` : last.replace(/-/g, ' ');
    return [
      { label: parent.label, to: parent.path },
      { label: hereLabel.charAt(0).toUpperCase() + hereLabel.slice(1), here: true },
    ];
  }

  const last = pathname.split('/').filter(Boolean).slice(-1)[0] || 'Page';
  return [{ label: last.replace(/-/g, ' '), here: true }];
}

/* ─── User popover (.user-menu) ───────────────────────────────────────── */

function MeMenu({ user, onClose }) {
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const role =
    Array.isArray(user?.admin_roles) && user.admin_roles.length
      ? user.admin_roles[0]
      : null;

  return (
    <div
      ref={ref}
      role="menu"
      // `.user-menu` (prototype lines 2616–2628):
      //   background surface, 1px sand border, lg radius (12px),
      //   shadow 0 12px 40px #2C3E5022, 0 2px 6px #2C3E501a,
      //   min-width 280 / max-width 320, padding 8px, z-index 200,
      //   open animation: opacity + translate(-6→0) + scale(0.98→1) over 160ms.
      style={{
        minWidth: 280,
        maxWidth: 320,
        boxShadow: '0 12px 40px #2C3E5022, 0 2px 6px #2C3E501A',
        transformOrigin: 'top right',
      }}
      className={cn(
        'absolute right-0 top-full mt-2 z-[200]',
        'bg-surface border border-sand rounded-lg p-2',
        'animate-[userMenuIn_160ms_ease]',
      )}
    >
      {/* `.user-menu .head` (2634–2642):
          grid 44px 1fr, gap 12px, padding 14px 12px, rounded-md (8px),
          background cream, margin-bottom 6px. */}
      <div
        className={cn(
          'grid grid-cols-[44px_1fr] items-center bg-cream rounded-md',
        )}
        style={{ gap: 12, padding: '14px 12px', marginBottom: 6 }}
      >
        <Avatar user={user} size={44} fontSize={16} />
        <div className="min-w-0">
          {/* `.user-menu .name` (2653–2657) */}
          <div
            className="font-condensed font-bold text-navy"
            style={{ fontSize: 15, lineHeight: 1.15 }}
          >
            {user?.admin_name || 'Admin'}
          </div>
          {/* `.user-menu .email` (2658–2662):
              11.5px text-light, margin-top 2px, ellipsis. */}
          {user?.admin_email && (
            <div
              className="text-text-light truncate"
              style={{ fontSize: 11.5, marginTop: 2 }}
            >
              {user.admin_email}
            </div>
          )}
          {/* `.user-menu .role-badge` (2663–2671):
              padding 2px 8px, teal-green-soft bg, teal-green-d text,
              Asap Condensed 10.5px bold uppercase letter-spacing 0.06em,
              border-radius pill. */}
          {role && (
            <span
              className={cn(
                'inline-block bg-teal-green-soft text-teal-green-dark',
                'font-condensed font-bold uppercase rounded-pill',
              )}
              style={{
                marginTop: 6,
                padding: '2px 8px',
                fontSize: 10.5,
                letterSpacing: '0.06em',
              }}
            >
              {role}
            </span>
          )}
        </div>
      </div>

      {/* `.user-menu .group` (2672) — padding 6px 2px */}
      <div style={{ padding: '6px 2px' }}>
        <MenuItem
          onClick={() => {
            onClose();
            navigate('/my-account');
          }}
          icon="settings"
          label="Account settings"
        />
      </div>

      {/* `.user-menu .divider` (2709–2712) — 1px sand, margin 4px 8px */}
      <div className="bg-sand" style={{ height: 1, margin: '4px 8px' }} />

      {/* Theme mode selector — three-state segmented (Light / Auto /
       *  Dark). Stays open after a click so the operator can see the
       *  page repaint into the new mode without losing the menu. */}
      <div style={{ padding: '6px 8px' }}>
        <ThemeModeRow />
      </div>

      {/* Text-size selector — UI scale (zoom) for readability. Grouped
       *  with Theme as an "appearance" pair, between the same dividers. */}
      <div style={{ padding: '6px 8px' }}>
        <FontSizeRow />
      </div>

      {/* `.user-menu .divider` (2709–2712) — 1px sand, margin 4px 8px */}
      <div className="bg-sand" style={{ height: 1, margin: '4px 8px' }} />

      {/* Redesign switcher — a plain action row, not a stateful
       *  toggle. The pill UI was misleading because the whole chrome
       *  rerenders the moment you flip it (the row itself disappears
       *  on v1), so a single-click action with directional copy
       *  ("Switch back to the old design") reads more honestly. */}
      <div style={{ padding: '6px 2px' }}>
        <RedesignSwitchRow onAfter={onClose} />
      </div>

      {/* `.user-menu .divider` (2709–2712) — 1px sand, margin 4px 8px */}
      <div className="bg-sand" style={{ height: 1, margin: '4px 8px' }} />

      {/* Sign out — direct child of .user-menu (not wrapped in a .group),
       *  matching the prototype's structure. */}
      <MenuItem
        onClick={() => {
          onClose();
          navigate('/logout');
        }}
        icon="back_arrow"
        label="Sign out"
        danger
      />
    </div>
  );
}

/* ─── Theme-mode selector row (Light / Auto / Dark segmented) ────── */
/* Same row footprint as MenuItem (padding 8/10, 13px, sand
 * hover) but instead of an action, the content is a 3-way segmented
 * control. "Auto" defers to the OS prefers-color-scheme media query.
 *
 * Note: dark mode is currently plumbing-only — clicking "Dark"
 * flips <html data-theme="dark"> and the CSS variable system
 * resolves correctly, but the `:root` + `[data-theme="dark"]` blocks
 * in themeTokens.css still hold identical values until the real
 * dark palette is designed. The selector ships now so the UX surface
 * is in place; the dark colors land in a follow-up. */
const THEME_OPTIONS = [
  { value: 'light',  label: 'Light' },
  { value: 'system', label: 'Auto'  },
  { value: 'dark',   label: 'Dark'  },
];

function ThemeModeRow() {
  const [mode, , setMode] = useThemeMode();
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="w-full flex items-center"
      style={{ gap: 10, padding: '6px 4px' }}
    >
      <span
        className="inline-flex shrink-0 text-text-muted"
        style={{ width: 15, height: 15 }}
        aria-hidden
      >
        <BrandIcon name="settings" size={15} />
      </span>
      <span className="text-text" style={{ fontSize: 13, flexShrink: 0 }}>
        Theme
      </span>
      <div
        className="ml-auto inline-flex bg-cream border border-sand"
        style={{
          borderRadius: 999,
          padding: 2,
        }}
      >
        {THEME_OPTIONS.map((opt) => {
          const active = opt.value === mode;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setMode(opt.value)}
              className={cn(
                'font-semibold cursor-pointer leading-none',
                active
                  ? 'bg-surface text-navy'
                  : 'bg-transparent text-text-muted hover:text-navy',
              )}
              style={{
                padding: '4px 10px',
                fontSize: 11.5,
                borderRadius: 999,
                border: 0,
                transition: 'all 140ms ease',
                ...(active && { boxShadow: '0 1px 2px rgb(var(--color-navy) / 0.15)' }),
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Text-size selector row (S / M / L / XL segmented) ─────────── */
/* Same row footprint as ThemeModeRow. Drives a UI zoom on #root via
 * useFontScale — the redesign hard-codes inline px sizes everywhere, so a
 * whole-UI zoom is the only way to scale text globally. `M` is the
 * untouched 100% design default. */
const FONT_SIZE_OPTIONS = [
  { value: 'sm', label: 'S',  aria: 'Small (90%)' },
  { value: 'md', label: 'M',  aria: 'Default (100%)' },
  { value: 'lg', label: 'L',  aria: 'Large (110%)' },
  { value: 'xl', label: 'XL', aria: 'Larger (120%)' },
];

function FontSizeRow() {
  const [scale, setScale] = useFontScale();
  return (
    <div
      role="radiogroup"
      aria-label="Text size"
      className="w-full flex items-center"
      style={{ gap: 10, padding: '6px 4px' }}
    >
      <span
        className="inline-flex shrink-0 items-center justify-center text-text-muted font-bold"
        style={{ width: 15, height: 15, fontSize: 13, lineHeight: 1 }}
        aria-hidden
      >
        A
      </span>
      <span className="text-text" style={{ fontSize: 13, flexShrink: 0 }}>
        Text size
      </span>
      <div
        className="ml-auto inline-flex bg-cream border border-sand"
        style={{ borderRadius: 999, padding: 2 }}
      >
        {FONT_SIZE_OPTIONS.map((opt) => {
          const active = opt.value === scale;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={opt.aria}
              title={opt.aria}
              onClick={() => setScale(opt.value)}
              className={cn(
                'font-semibold cursor-pointer leading-none',
                active
                  ? 'bg-surface text-navy'
                  : 'bg-transparent text-text-muted hover:text-navy',
              )}
              style={{
                padding: '4px 9px',
                fontSize: 11.5,
                borderRadius: 999,
                border: 0,
                transition: 'all 140ms ease',
                ...(active && { boxShadow: '0 1px 2px rgb(var(--color-navy) / 0.15)' }),
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Redesign switch row (single-action, dynamic copy) ─────────── */
/* Renders as a sibling of "Account settings" — same item recipe,
 * brand swirl icon, directional label. Clicking dispatches the
 * flag toggle and closes the menu (the page rerenders into the
 * other chrome immediately; the dropdown's container goes away on
 * v1, so closing first keeps the transition tidy). */
function RedesignSwitchRow({ onAfter }) {
  const [v2, setV2] = useRedesignFlag();
  const label = v2
    ? 'Switch back to the old design'
    : 'Switch to the new design!';
  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        setV2(!v2);
        onAfter?.();
      }}
      className={cn(
        'w-full flex items-center text-left rounded-md',
        'text-text hover:bg-cream hover:text-navy',
      )}
      style={{
        gap: 10,
        padding: '8px 10px',
        fontSize: 13,
        transition: 'background 140ms ease',
      }}
    >
      <span
        className="inline-flex shrink-0 text-text-muted"
        style={{ width: 15, height: 15 }}
      >
        <BrandIcon name="swirl" size={15} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
    </button>
  );
}

/** `.user-menu .item` (2713–2728) and `.item.danger` (2729–2731):
 *  flex gap 10, padding 8 10, rounded-md, 13px text, transition background
 *  140ms ease. Icons are 15×15, color text-muted (or coral-d when danger). */
function MenuItem({ onClick, icon, label, danger }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'w-full flex items-center text-left rounded-md',
        danger
          ? 'text-coral-dark hover:bg-coral-soft'
          : 'text-text hover:bg-cream hover:text-navy',
      )}
      style={{
        gap: 10,
        padding: '8px 10px',
        fontSize: 13,
        transition: 'background 140ms ease',
      }}
    >
      <span
        className={cn('inline-flex shrink-0', danger ? 'text-coral-dark' : 'text-text-muted')}
        style={{ width: 15, height: 15 }}
      >
        <BrandIcon name={icon} size={15} />
      </span>
      {label}
    </button>
  );
}

/* ─── TopBar ──────────────────────────────────────────────────────────── */

export default function TopBar({ onOpenMobileNav }) {
  const location = useLocation();
  const user = SessionHelper.currentUser?.() || null;
  const [menuOpen, setMenuOpen] = useState(false);

  const crumbs = crumbsFor(location.pathname);

  return (
    <header
      // `.topbar` (123–130):
      //   height 56px, padding 0 20px, surface bg, sand bottom border,
      //   position relative, z-index 50.
      //
      // The header's z-index is intentionally above every V2 edit
      // page's sticky Discard/Save action bar (`zIndex: 20` across
      // ChallengeAddEditPageV2, AudioBookAddEditPageV2, ApiKeyFormPageV2,
      // CategoryAddEditPageV2, SubCategoryAddEditPageV2, RoleFormPageV2,
      // DailyReflectionFormPageV2, BackgroundAddEditPageV2). At z-index
      // 10 the action bar's stacking context beat the TopBar's,
      // clipping the user-menu dropdown (`.user-menu`, z-[200] within
      // the TopBar) behind the action bar — see Monday 12126809417.
      // The dropdown's z-200 is only compared against siblings inside
      // the TopBar's stacking context, so the TopBar itself has to
      // outrank the action bar at the parent level. 50 leaves room
      // below modals (typically z-1000+) and other future global
      // overlays.
      className={cn(
        'flex items-center bg-surface border-b border-sand relative shrink-0',
      )}
      style={{ height: 56, padding: '0 20px', zIndex: 50 }}
    >
      {/* Mobile-only hamburger (no equivalent in prototype HTML; needed for
       *  the React layout's collapsing drawer). */}
      {onOpenMobileNav && (
        <button
          type="button"
          onClick={onOpenMobileNav}
          aria-label="Open navigation"
          className="md:hidden w-9 h-9 -ml-2 mr-2 grid place-items-center rounded-md text-navy hover:bg-cream"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}

      {/* `.ws` (131–136): flex, gap 10, padding-right 18, margin-right 4,
       *  border-right line, height 32. The whole cluster is a Link → /dashboard. */}
      <Link
        to="/dashboard"
        className={cn(
          'flex items-center border-r border-line shrink-0 no-underline',
        )}
        style={{ gap: 10, paddingRight: 18, marginRight: 4, height: 32 }}
      >
        {/* `.ws-mark` (137–141) + child SVG sized 30×30 (142–143) */}
        <span
          className="grid place-items-center shrink-0"
          style={{ width: 34, height: 34 }}
        >
          <TtsSwirl style={{ width: 30, height: 30 }} />
        </span>
        {/* `.ws-name` (144–147). Hidden below 900px per prototype's @media (2471). */}
        <span
          className="font-condensed font-bold text-navy max-[899px]:hidden"
          style={{ fontSize: 15 }}
        >
          The Tapping Solution
        </span>
        {/* `.ws .caret` (148) — text-muted 10px. Decorative (workspace-switcher
         *  affordance is single-tenant for now, so click-through still goes to
         *  /dashboard via the parent Link). */}
        <span
          aria-hidden
          className="text-text-muted max-[899px]:hidden"
          style={{ fontSize: 10 }}
        >
          ▾
        </span>
      </Link>

      {/* `.crumbs` (150–153): flex, gap 10, padding 0 16, text-light, 13.5px */}
      <nav
        aria-label="Breadcrumb"
        className="flex items-center text-text-light min-w-0"
        style={{ gap: 10, padding: '0 16px', fontSize: 13.5 }}
      >
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {/* `.crumbs .sep` (154) — #2C3E5040 */}
            {i > 0 && (
              <span aria-hidden style={{ color: '#2C3E5040' }}>›</span>
            )}
            {c.here ? (
              /* `.crumbs .here` (155): navy, font-weight 600 */
              <span className="text-navy" style={{ fontWeight: 600 }}>
                {c.label}
              </span>
            ) : c.to ? (
              /* `.crumbs > span:not(.here):not(.sep)` (158–159):
                 cursor pointer, transition color 140ms ease, hover teal-green */
              <Link
                to={c.to}
                className="text-text-light hover:text-teal-green no-underline"
                style={{ transition: 'color 140ms ease' }}
              >
                {c.label}
              </Link>
            ) : (
              <span>{c.label}</span>
            )}
          </React.Fragment>
        ))}
      </nav>

      {/* `.top-search` (161–168): ml-auto, mr 12, gap 10, cream bg,
       *  1px sand border, pill radius, padding 8 14, width 320.
       *  Click anywhere on the bar opens the command palette (matches the
       *  prototype's behavior — line 10661, `bar.addEventListener('click', open)`).
       *  The input is read-only to keep the bar purely a trigger. */}
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent('cms:open-palette'))}
        aria-label="Open command palette"
        className={cn(
          'hidden md:flex items-center bg-cream border border-sand rounded-pill',
          'cursor-pointer text-left',
          'hover:border-line-strong',
        )}
        style={{
          marginLeft: 'auto',
          marginRight: 12,
          gap: 10,
          padding: '8px 14px',
          width: 320,
          transition: 'all 160ms ease',
        }}
      >
        <BrandIcon name="search" size={14} className="text-text-muted" />
        {/* Placeholder rendered as a span (not an <input>) — the palette owns
         *  the actual text-entry experience. Same color/size as prototype. */}
        <span
          className="flex-1 min-w-0 text-text-muted truncate"
          style={{ fontSize: 13 }}
        >
          Search across all content…
        </span>
        {/* `.kbd-hint` (178–188): JetBrains Mono 10.5px / 600, padding 2 7,
         *  surface bg, sand border, sm radius, line-height 1.4. */}
        <span
          className={cn(
            'shrink-0 font-mono font-semibold text-text-muted',
            'bg-surface border border-sand rounded-sm',
          )}
          style={{
            fontSize: 10.5,
            padding: '2px 7px',
            lineHeight: 1.4,
          }}
        >
          {/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}
        </span>
      </button>

      {/* `.me` (198–204): flex, gap 9, margin-left 6, padding 4 12 4 4,
       *  1px sand border, pill radius, surface bg, transition all 160ms ease.
       *  `.me:hover` (205): bg cream. */}
      <div className="relative" style={{ marginLeft: 6 }}>
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className={cn(
            'flex items-center border border-sand rounded-pill bg-surface',
            'hover:bg-cream cursor-pointer',
          )}
          style={{
            gap: 9,
            padding: '4px 12px 4px 4px',
            transition: 'all 160ms ease',
          }}
        >
          {/* `.avatar` (206–212): 26px disc, teal-green, white,
           *  Asap Condensed 12px bold. */}
          <Avatar user={user} size={26} fontSize={12} />
          {/* `.me-name` (213): 13px / 600 / navy. Hidden below 900px (2477). */}
          <span
            className="text-navy max-[899px]:hidden"
            style={{ fontSize: 13, fontWeight: 600 }}
          >
            {firstName(user) || 'Admin'}
          </span>
        </button>
        {menuOpen && <MeMenu user={user} onClose={() => setMenuOpen(false)} />}
      </div>
    </header>
  );
}
